<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, useAttrs } from 'vue'

const props = withDefaults(defineProps<Props>(), {
  label: '',
  id: '',
  modelValue: '',
  error: false,
  errorMessage: '',
  wrapperClass: '',
  isBusy: false,
})

interface Props {
  label?: string
  id?: string
  modelValue?: string
  error?: boolean
  errorMessage?: string
  wrapperClass?: string
  isBusy?: boolean
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'pin-complete', value: string): void
  (e: 'blur', ev: FocusEvent): void
  (e: 'focus', ev: FocusEvent): void
}>()

const PIN_LEN = 4

function generateId(): string {
  if (
    typeof globalThis !== 'undefined' &&
    'crypto' in globalThis &&
    globalThis.crypto?.randomUUID
  ) {
    return globalThis.crypto.randomUUID()
  }

  return `input-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

const inputId = ref<string>(props.id || generateId())

const model = computed<string>({
  get: () => props.modelValue ?? '',
  set: (val) => emit('update:modelValue', val),
})

const attrs = useAttrs()

const describedBy = computed(() => {
  return props.error && props.errorMessage ? `${inputId.value}-feedback` : undefined
})

function enterNumber(n: number): void {
  if (!Number.isInteger(n) || n < 0 || n > 9) return

  const current = model.value ?? ''
  if (current.length >= PIN_LEN) return

  const next = `${current}${n}`
  model.value = next

  if (next.length === PIN_LEN) {
    emit('pin-complete', next)
  }
}

let deleteHoldTimeout: number | null = null
let deleteHoldInterval: number | null = null

function clearDeleteHoldTimers(): void {
  if (deleteHoldTimeout !== null) {
    window.clearTimeout(deleteHoldTimeout)
    deleteHoldTimeout = null
  }
  if (deleteHoldInterval !== null) {
    window.clearInterval(deleteHoldInterval)
    deleteHoldInterval = null
  }
}

function onDeletePressStart(ev: PointerEvent): void {
  ev.preventDefault()
  clearDeleteHoldTimers()
  deleteNumber()
  deleteHoldTimeout = window.setTimeout(() => {
    deleteHoldInterval = window.setInterval(() => {
      deleteNumber()
      if (!(model.value ?? '').length) {
        clearDeleteHoldTimers()
      }
    }, 90)
  }, 350)
}

function onDeletePressEnd(): void {
  clearDeleteHoldTimers()
}

onBeforeUnmount(() => {
  clearDeleteHoldTimers()
})

function deleteNumber(): void {
  const current = model.value ?? ''
  if (!current.length) return

  model.value = current.slice(0, -1)
}
</script>

<template>
  <div :class="wrapperClass">
    <div v-if="props.label" class="pin-label">
      {{ props.label }}
    </div>

    <div class="pin-boxes" aria-hidden="true">
      <span
        v-for="i in 4"
        :key="i"
        class="pin-box"
        :class="{ filled: i <= (model?.length ?? 0) }"
      />
    </div>

    <input
      v-bind="attrs"
      :id="inputId"
      v-model="model"
      inputmode="numeric"
      autocomplete="one-time-code"
      pattern="[0-9]*"
      type="hidden"
      class="pin-input"
      :aria-invalid="props.error ? 'true' : 'false'"
      :aria-describedby="describedBy"
      @blur="emit('blur', $event)"
      @focus="emit('focus', $event)"
    />

    <div class="pin-keys">
      <button
        v-for="n in 9"
        :key="n"
        type="button"
        class="btn btn-primary py-12"
        :disabled="props.isBusy"
        @click="enterNumber(n)"
      >
        {{ n }}
      </button>
      <slot>
        <span></span>
      </slot>
      <button
        type="button"
        class="btn btn-primary py-12"
        :disabled="props.isBusy"
        @click="enterNumber(0)"
      >
        0
      </button>
      <button
        class="btn btn-danger py-12"
        type="button"
        :disabled="props.isBusy"
        @pointerdown="onDeletePressStart"
        @pointerup="onDeletePressEnd"
        @pointercancel="onDeletePressEnd"
        @pointerleave="onDeletePressEnd"
      >
        ⌫
      </button>
    </div>
    <slot name="underButtons" />

    <div
      v-if="props.error && props.errorMessage"
      :id="`${inputId}-feedback`"
      class="invalid-feedback d-block mt-12"
    >
      <b>{{ props.errorMessage }}</b>
    </div>
  </div>
</template>
